import React from 'react'
import Header from '../components/Header'
import Navigation from '../components/Navigation'

import { useEffect, useMemo, useState } from 'react'
import * as XLSX from 'xlsx'
import templateExcel from '../assests/批量导入.xlsx?url'

type BirthType = 'single' | 'twins' | 'triplets' | 'quadruplets'
type DeliveryMethod = 'normal' | 'difficult' | 'miscarriage'

interface HistoryVacationData {
  birthType?: string
  hasExtendedDays?: boolean
  rewardLeave?: boolean
  deliveryMethod?: string
  pregnancyDuration?: number
}

interface HistoryAllowanceData {
  monthlyAverageSalary?: number
  socialInsuranceBase?: number
  governmentSubsidy?: number
  [key: string]: unknown
}

interface HistoryRecordLite {
  id: string
  employeeName: string
  employeeId: string
  city: string
  cityName?: string
  startDate: string
  endDate: string
  totalDays: number
  companyCompensation: number
  employeeCompensation?: number
  calculatedAt: string
  recordType?: string
  receivedTime?: string
  submissionTime?: string
  vacationData?: HistoryVacationData
  allowanceData?: HistoryAllowanceData
}

interface ComputedResult {
  totalDays: number
  startDate: string
  endDate: string
  breakdown: {
    baseDays: number
    extendedDays: number
    difficultBirthDays: number
    multipleBirthDays: number
    miscarriageDays: number
  }
  allowance: {
    allowanceDays: number
    dailyRate: number
    baseByAvgSalary: number
    government: number
    chosenD: number
    paidWageA: number
    companyCompensation: number
    employeeRefundAmount: number
  }
  details: string[]
}

interface NormalizedRow {
  key: string
  fromExcel: {
    name: string
    id: string
    city: string
    startDate: string
    birthType: BirthType
    hasExtendedDays: boolean
    deliveryMethod: DeliveryMethod
    pregnancyDuration?: number
    totalAllowanceDays?: number
    avgSalary12Months?: number
    governmentAmount?: number
    monthlyBaseSalary?: number
    declaredSalary?: number
    companyCompensation?: number
    employeeRefundAmount?: number
    monthlyAverageSalary?: number
    socialInsuranceBase?: number
    paidMaternityWage?: number
    paidSalary?: number
    adjustedMonthlyBaseSalary?: number
    employeeInsurance?: number
    supplementaryFund?: number
    unionFee?: number
    espp?: number
    spotOn?: number
    flexibleBenefit?: number
    otherDeductions?: number
    employerESPP?: number
    employerSupplementaryFund?: number
    personalIncomeTax?: number
    insuranceBefore?: number
    insuranceAfter?: number
    crossApril?: boolean
    crossJuly?: boolean
    receivedTime?: string
    submissionTime?: string
  }
  fromHistory?: {
    name: string
    id: string
    city: string
    startDate: string
    birthType?: BirthType
    hasExtendedDays?: boolean
    deliveryMethod?: DeliveryMethod
    pregnancyDuration?: number
    totalAllowanceDays?: number
    avgSalary12Months?: number
    governmentAmount?: number
    monthlyBaseSalary?: number
    declaredSalary?: number
    companyCompensation?: number
    employeeRefundAmount?: number
    monthlyAverageSalary?: number
    socialInsuranceBase?: number
    paidMaternityWage?: number
    paidSalary?: number
    adjustedMonthlyBaseSalary?: number
    employeeInsurance?: number
    supplementaryFund?: number
    unionFee?: number
    espp?: number
    spotOn?: number
    flexibleBenefit?: number
    otherDeductions?: number
    employerESPP?: number
    employerSupplementaryFund?: number
    personalIncomeTax?: number
    insuranceBefore?: number
    insuranceAfter?: number
    crossApril?: boolean
    crossJuly?: boolean
    receivedTime?: string
    submissionTime?: string
  }
  chosen: 'excel' | 'history'
  hasDiff: boolean
  selected: boolean
  expanded: boolean
  resultExpanded?: boolean
  result?: ComputedResult
}

const cityNameToCodeMap: { [k: string]: string } = {
  北京: 'BJ', 佛山: 'FS', 广州: 'GZ', 深圳: 'SZ', 珠海: 'ZH', 武汉: 'WH',
  '苏州（园区）': 'SZ2', 苏州: 'SU', 成都: 'CD', 重庆: 'CQ', 天津: 'TJ', 厦门: 'XM', 上海: 'SH', 青岛: 'QD', 绍兴: 'SX'
}
const codeToCityName: { [k: string]: string } = Object.entries(cityNameToCodeMap).reduce((m, [n, c]) => { m[c] = n; return m }, {} as { [k: string]: string })
const isPayrollCity = (code: string) => code === 'SZ'

const ymd = (d?: string | Date) => {
  if (!d) return ''
  const x = typeof d === 'string' ? new Date(d) : d
  if (isNaN(x.getTime())) return ''
  const y = x.getFullYear(); const m = String(x.getMonth()+1).padStart(2,'0'); const dd = String(x.getDate()).padStart(2,'0')
  return `${y}-${m}-${dd}`
}
const keyOf = (name: string, id: string) => `${name}__${id}`

const BatchProcessor: React.FC = () => {
  const [rows, setRows] = useState<NormalizedRow[]>([])
  const [historyMap, setHistoryMap] = useState<Map<string, HistoryRecordLite>>(new Map())
  const [selectAll, setSelectAll] = useState(false)

  useEffect(() => {
    try {
      const keys = ['hr_maternity_history','allowanceHistory','maternityHistoryRecords']
      const all: HistoryRecordLite[] = []
      keys.forEach(k=>{ const s=localStorage.getItem(k); if(!s) return; try{ const p=JSON.parse(s); if(Array.isArray(p)) all.push(...p)}catch(err){ console.warn('解析历史记录失败', k, err) }})
      const m = new Map<string, HistoryRecordLite>()
      all.forEach(r=>{ const k = keyOf(r.employeeName||'', r.employeeId||''); if(k.trim()) m.set(k, r) })
      setHistoryMap(m)
    } catch(e){ console.error('加载历史失败', e) }
  }, [])

  const handleDownload = () => {
    const a = document.createElement('a')
    a.href = templateExcel
    a.download = '批量导入模板.xlsx'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const buildRow = (eRow: NormalizedRow['fromExcel']): NormalizedRow => {
    const k = keyOf(eRow.name, eRow.id)
    const his = historyMap.get(k)
    let fromHistory: NormalizedRow['fromHistory'] | undefined
    if (his){
      const ad = his.allowanceData || {}
      fromHistory = {
        name: his.employeeName,
        id: his.employeeId,
        city: his.city,
        startDate: ymd(his.startDate),
        birthType: (his.vacationData?.birthType as BirthType) || 'single',
        hasExtendedDays: !!his.vacationData?.hasExtendedDays,
        deliveryMethod: (his.vacationData?.deliveryMethod as DeliveryMethod) || 'normal',
        pregnancyDuration: his.vacationData?.pregnancyDuration,
        totalAllowanceDays: his.totalDays,
        avgSalary12Months: ad.avgSalary12Months as number | undefined,
        governmentAmount: ad.governmentSubsidy as number | undefined,
        monthlyBaseSalary: ad.monthlyBaseSalary as number | undefined,
        declaredSalary: ad.declaredSalary as number | undefined,
        companyCompensation: his.companyCompensation as number | undefined,
        employeeRefundAmount: his.employeeCompensation as number | undefined,
        monthlyAverageSalary: ad.monthlyAverageSalary as number | undefined,
        socialInsuranceBase: ad.socialInsuranceBase as number | undefined,
        paidMaternityWage: ad.paidMaternityWage as number | undefined,
        paidSalary: ad.paidSalary as number | undefined,
        adjustedMonthlyBaseSalary: ad.adjustedMonthlyBaseSalary as number | undefined,
        employeeInsurance: ad.employeeInsurance as number | undefined,
        supplementaryFund: ad.supplementaryFund as number | undefined,
        unionFee: ad.unionFee as number | undefined,
        espp: ad.espp as number | undefined,
        spotOn: ad.spotOn as number | undefined,
        flexibleBenefit: ad.flexibleBenefit as number | undefined,
        otherDeductions: ad.otherDeductions as number | undefined,
        employerESPP: ad.employerESPP as number | undefined,
        employerSupplementaryFund: ad.employerSupplementaryFund as number | undefined,
        personalIncomeTax: ad.personalIncomeTax as number | undefined,
        insuranceBefore: ad.insuranceBefore as number | undefined,
        insuranceAfter: ad.insuranceAfter as number | undefined,
        crossApril: ad.crossApril as boolean | undefined,
        crossJuly: ad.crossJuly as boolean | undefined,
        receivedTime: his.receivedTime,
        submissionTime: ad.maternitySubmissionDate as string | undefined,
      }
    }
    const hasDiff = !!fromHistory && (
      fromHistory.city !== eRow.city || ymd(fromHistory.startDate)!==ymd(eRow.startDate) ||
      (fromHistory.birthType||'single')!==eRow.birthType || !!fromHistory.hasExtendedDays!==!!eRow.hasExtendedDays ||
      (fromHistory.deliveryMethod||'normal')!==eRow.deliveryMethod || (fromHistory.pregnancyDuration||0)!==(eRow.pregnancyDuration||0)
    )
    return { key:k, fromExcel:eRow, fromHistory, chosen: 'excel', hasDiff, selected:true, expanded:false }
  }

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    try {
      // 读取Excel文件
      const data = await f.arrayBuffer()
      const workbook = XLSX.read(data, { type: 'array' })
      const sheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[sheetName]
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][]
      
      // 跳过表头，解析数据行
      const rows = jsonData.slice(1).filter(row => row.length > 0)
      
      const samples: NormalizedRow['fromExcel'][] = rows.map(row => {
        // 解析Excel列（根据HistoryRecords导出的列顺序）
        // 0: 员工姓名, 1: 员工工号, 2: 所在城市, 3: 产假开始日期
        // 4: 产假结束日期, 5: 产假总天数, 6: 生育类型, 7: 生育方式
        // 8: 晚育/生育/奖励假, 9: 计算时间, 10: 怀孕周数(可选)
        // 后续列: 政府津贴, 应享受补贴金额, 实发薪资, 津贴天数, 津贴补差, 返还金额等
        
        const name = String(row[0] || '').trim()
        const id = String(row[1] || '').trim()
        const cityName = String(row[2] || '').trim()
        const city = cityNameToCodeMap[cityName] || cityName
        const startDate = row[3] ? parseExcelDate(row[3]) : ymd(new Date())
        // row[4] 是产假结束日期，跳过
        // row[5] 是产假总天数，跳过
        
        const birthTypeMap: {[k: string]: BirthType} = {'单胎': 'single', '双胞胎': 'twins', '三胞胎': 'triplets', '四胞胎': 'quadruplets'}
        const birthType = birthTypeMap[String(row[6] || '单胎')] || 'single'
        
        const deliveryMethodMap: {[k: string]: DeliveryMethod} = {'顺产': 'normal', '正常分娩': 'normal', '正常': 'normal', '难产': 'difficult', '流产': 'miscarriage'}
        const deliveryMethod = deliveryMethodMap[String(row[7] || '正常')] || 'normal'
        
        const hasExtendedDays = String(row[8] || '').includes('是') || String(row[8] || '').includes('有')
        // row[9] 是计算时间，跳过
        
        // 怀孕周数：检查row[10]是否包含"周"字
        const pregnancyDurationStr = String(row[10] || '')
        const pregnancyDuration = deliveryMethod === 'miscarriage' && pregnancyDurationStr.includes('周') 
          ? Number(pregnancyDurationStr.replace(/[^0-9]/g, '')) 
          : undefined
        
        // 解析津贴相关字段（根据导出的列顺序）
        const parseNumber = (val: unknown): number | undefined => {
          if (!val) return undefined
          const str = String(val).replace(/[¥,]/g, '').replace(/天$/, '').replace(/周$/, '').trim()
          const num = Number(str)
          return isNaN(num) ? undefined : num
        }
        
        const parseBoolean = (val: unknown): boolean | undefined => {
          if (!val) return undefined
          const str = String(val).trim()
          if (str === '是' || str === 'true' || str === '1') return true
          if (str === '否' || str === 'false' || str === '0') return false
          return undefined
        }
        
        const parseString = (val: unknown): string | undefined => {
          if (!val) return undefined
          const str = String(val).trim()
          return str || undefined
        }
        
        // 使用智能列查找来解析所有字段
        const findColumnValue = (headerPattern: string): number | undefined => {
          const headerRow = jsonData[0] as unknown[]
          const colIdx = headerRow.findIndex(h => String(h || '').includes(headerPattern))
          return colIdx >= 0 ? parseNumber(row[colIdx]) : undefined
        }
        
        const findStringValue = (headerPattern: string): string | undefined => {
          const headerRow = jsonData[0] as unknown[]
          const colIdx = headerRow.findIndex(h => String(h || '').includes(headerPattern))
          return colIdx >= 0 ? parseString(row[colIdx]) : undefined
        }
        
        const findBooleanValue = (headerPattern: string): boolean | undefined => {
          const headerRow = jsonData[0] as unknown[]
          const colIdx = headerRow.findIndex(h => String(h || '').includes(headerPattern))
          return colIdx >= 0 ? parseBoolean(row[colIdx]) : undefined
        }
        
        // 根据列标题查找对应的值 - 基础字段
        const governmentAmount = findColumnValue('政府津贴') || findColumnValue('政府补贴')
        const avgSalary12Months = findColumnValue('12个月平均工资')
        const monthlyAverageSalary = findColumnValue('应享受补贴金额')
        const monthlyBaseSalary = findColumnValue('基本工资（跨四月）')
        const socialInsuranceBase = findColumnValue('实发薪资')
        const totalAllowanceDays = findColumnValue('产假总天数') || findColumnValue('津贴天数')
        const companyCompensation = findColumnValue('津贴补差') || findColumnValue('津贴补差金额')
        const employeeRefundAmount = findColumnValue('返还金额')
        const declaredSalary = findColumnValue('单位申报上年度月均')
        
        // 扩展字段 - 工资相关
        const paidMaternityWage = findColumnValue('产假期间发放工资')
        const paidSalary = findColumnValue('员工基本工资')
        const adjustedMonthlyBaseSalary = findColumnValue('调整后基本工资')
        
        // 扣减项
        const employeeInsurance = findColumnValue('五险一金（个人）')
        const supplementaryFund = findColumnValue('补充公积金（个人）')
        const unionFee = findColumnValue('工会费（个人）')
        const espp = findColumnValue('ESPP')
        const spotOn = findColumnValue('spot on')
        const flexibleBenefit = findColumnValue('弹性福利')
        const otherDeductions = findColumnValue('其他扣减')
        
        // 雇主项
        const employerESPP = findColumnValue('月度ESPP')
        const employerSupplementaryFund = findColumnValue('月度补充公积金')
        const personalIncomeTax = findColumnValue('月度工会费')
        
        // 社保基数
        const insuranceBefore = findColumnValue('七月前社保基数')
        const insuranceAfter = findColumnValue('七月后社保基数')
        
        // 布尔字段
        const crossApril = findBooleanValue('是否跨四月')
        const crossJuly = findBooleanValue('是否跨七月')
        
        // 日期字段
        const receivedTime = findStringValue('提交核定表时间')
        const submissionTime = findStringValue('提交产假时间') || findStringValue('产假提交日期')
        
        return {
          name,
          id,
          city,
          startDate,
          birthType,
          hasExtendedDays,
          deliveryMethod,
          pregnancyDuration,
          totalAllowanceDays,
          avgSalary12Months,
          governmentAmount,
          monthlyBaseSalary,
          declaredSalary,
          companyCompensation,
          employeeRefundAmount,
          // 扩展字段
          monthlyAverageSalary,
          socialInsuranceBase,
          paidMaternityWage,
          paidSalary,
          adjustedMonthlyBaseSalary,
          employeeInsurance,
          supplementaryFund,
          unionFee,
          espp,
          spotOn,
          flexibleBenefit,
          otherDeductions,
          employerESPP,
          employerSupplementaryFund,
          personalIncomeTax,
          insuranceBefore,
          insuranceAfter,
          crossApril,
          crossJuly,
          receivedTime,
          submissionTime,
        }
      }).filter(item => item.name && item.id) // 过滤掉空行
      
      const normalized = samples.map(buildRow)
      setRows(normalized)
      setSelectAll(true)
    } catch (error) {
      console.error('Excel解析失败:', error)
      alert('Excel文件解析失败，请检查文件格式是否正确')
    } finally {
      e.currentTarget.value = ''
    }
  }
  
  // Excel日期解析辅助函数
  const parseExcelDate = (value: unknown): string => {
    if (typeof value === 'number') {
      // Excel日期是从1900-01-01开始的天数
      const date = XLSX.SSF.parse_date_code(value)
      return `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`
    }
    if (typeof value === 'string') {
      // 尝试解析字符串日期
      const d = new Date(value)
      if (!isNaN(d.getTime())) {
        return ymd(d)
      }
    }
    return ymd(new Date())
  }

  const toggleExpand = (key: string) => setRows(prev=>prev.map(r=>r.key===key?{...r, expanded:!r.expanded}:r))
  const choose = (key:string, src:'excel'|'history') => setRows(prev=>prev.map(r=>r.key===key?{...r, chosen:src}:r))
  const onSelectAll = () => { const n = !selectAll; setSelectAll(n); setRows(prev=>prev.map(r=>({...r, selected:n}))) }
  const onSelectOne = (key:string) => { setRows(prev=>{ const next = prev.map(r=>r.key===key?{...r, selected:!r.selected}:r); setSelectAll(next.every(x=>x.selected)); return next }) }
  
  // 格式化数字显示，处理NaN、undefined和0值
  const formatNumberDisplay = (val: number | undefined): string => {
    if (val === undefined || val === null) return '-'
    if (isNaN(val)) return '-'
    if (val === 0) return '-'
    return String(val)
  }
  const handleSave = async () => {
    const targets = rows.filter(r => r.selected)
    if (targets.length === 0) {
      alert('请至少选择一条记录')
      return
    }

    try {
      // 构建要保存的历史记录
      const newRecords = targets.map(r => {
        const src = r.chosen === 'excel' ? r.fromExcel : (r.fromHistory || r.fromExcel)
        
        // 如果选择了历史记录且历史记录存在，使用历史记录的ID
        const existingHistory = r.fromHistory && r.chosen === 'history' ? historyMap.get(r.key) : null
        
        const record: HistoryRecordLite = {
          id: existingHistory?.id || `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          employeeName: src.name,
          employeeId: src.id,
          city: src.city,
          cityName: codeToCityName[src.city] || src.city,
          startDate: src.startDate,
          endDate: existingHistory?.endDate || src.startDate,
          totalDays: src.totalAllowanceDays || 0,
          companyCompensation: src.companyCompensation || 0,
          employeeCompensation: src.employeeRefundAmount,
          calculatedAt: new Date().toISOString(),
          recordType: 'allowance',
          receivedTime: src.receivedTime,
          submissionTime: src.submissionTime,
          vacationData: {
            birthType: src.birthType,
            hasExtendedDays: src.hasExtendedDays,
            rewardLeave: false,
            deliveryMethod: src.deliveryMethod,
            pregnancyDuration: src.pregnancyDuration,
          },
          allowanceData: {
            avgSalary12Months: src.avgSalary12Months,
            monthlyAverageSalary: src.monthlyAverageSalary || src.avgSalary12Months,
            monthlyBaseSalary: src.monthlyBaseSalary,
            socialInsuranceBase: src.socialInsuranceBase || src.monthlyBaseSalary,
            governmentSubsidy: src.governmentAmount,
            declaredSalary: src.declaredSalary,
            paidMaternityWage: src.paidMaternityWage,
            paidSalary: src.paidSalary,
            adjustedMonthlyBaseSalary: src.adjustedMonthlyBaseSalary,
            employeeInsurance: src.employeeInsurance,
            supplementaryFund: src.supplementaryFund,
            unionFee: src.unionFee,
            espp: src.espp,
            spotOn: src.spotOn,
            flexibleBenefit: src.flexibleBenefit,
            otherDeductions: src.otherDeductions,
            employerESPP: src.employerESPP,
            employerSupplementaryFund: src.employerSupplementaryFund,
            personalIncomeTax: src.personalIncomeTax,
            insuranceBefore: src.insuranceBefore,
            insuranceAfter: src.insuranceAfter,
            crossApril: src.crossApril,
            crossJuly: src.crossJuly,
            maternitySubmissionDate: src.submissionTime,
          },
        }
        return record
      })

      // 根据选择的合并逻辑保存到历史记录
      const key = 'hr_maternity_history'
      const existed = localStorage.getItem(key)
      const existingRecords = existed ? (JSON.parse(existed) as HistoryRecordLite[]) : []
      
      // 合并逻辑：更新或新增
      // 1. 先过滤掉要更新的记录（根据employeeId匹配）
      const employeeIds = new Set(newRecords.map(r => r.employeeId))
      const filteredRecords = existingRecords.filter(r => !employeeIds.has(r.employeeId))
      
      // 2. 添加新记录（更新的记录会替换旧记录）
      const merged = [...filteredRecords, ...newRecords]
      localStorage.setItem(key, JSON.stringify(merged))
      
      const updatedCount = existingRecords.length - filteredRecords.length
      const newCount = newRecords.length - updatedCount
      
      if (updatedCount > 0 && newCount > 0) {
        alert(`成功更新 ${updatedCount} 条记录，新增 ${newCount} 条记录`)
      } else if (updatedCount > 0) {
        alert(`成功更新 ${updatedCount} 条记录`)
      } else {
        alert(`成功新增 ${newCount} 条记录`)
      }
      
      // 清空当前导入的数据
      setRows([])
      setSelectAll(false)
    } catch (error) {
      console.error('保存历史记录失败:', error)
      alert('保存失败，请重试')
    }
  }

  const computedCount = useMemo(()=>rows.filter(r=>r.result).length,[rows])

  return (
    <div>
      <Header />
      <Navigation />
      
      <div className="container">
        <header className="header">
          <div className="header-content">
            <i className="fas fa-users header-icon" />
            <h1>批量数据导入</h1>
            <p className="subtitle">批量导入历史记录数据，快速建立员工产假津贴档案</p>
          </div>
        </header>

        <main className="main-content">
          <div className="calculator-card">
            <div className="card-header">
              <h2><i className="fas fa-upload" /> 模板与导入</h2>
            </div>
            <div className="calculator-form">
              <div className="filter-row">
                <div className="form-group">
                  <label className="form-label">模板下载</label>
                  <button className="batch-export-btn" onClick={handleDownload}>
                    <i className="fas fa-download" /> 下载模板
                  </button>
                </div>
                <div className="form-group">
                  <label className="form-label">上传Excel</label>
                  <input className="form-input" type="file" accept=".xlsx,.xls" onChange={onUpload} />
                </div>
              </div>

              {rows.length>0 && (
                <div className="search-summary" style={{ marginTop: 8 }}>
                  <div className="summary-item"><i className="fas fa-list-ul" /> 已解析 {rows.length} 条</div>
                  <div className="batch-actions">
                    <label style={{ marginRight: 12 }}>
                      <input type="checkbox" checked={selectAll} onChange={onSelectAll} /> 全选
                    </label>
                    <button className="batch-export-btn" onClick={handleSave}>
                      <i className="fas fa-save" /> 确认并保存
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {rows.length>0 && (
            <div className="history-table-container" style={{ marginTop: 16 }}>
              <div className="calculator-card">
                <div className="card-header">
                  <h2><i className="fas fa-table" /> 导入数据对比</h2>
                </div>
                <div className="table-responsive">
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th style={{ width: 48 }}>
                          {/* 展开图标列，保持标题空白以简洁 */}
                        </th>
                        <th><input type="checkbox" checked={selectAll} onChange={onSelectAll} title="全选/取消全选" /></th>
                        <th>姓名</th>
                        <th>工号</th>
                        <th>城市</th>
                        <th>产假开始</th>
                        <th>分娩方式</th>
                        <th>生育类型</th>
                        <th>对比</th>
                        <th>选择</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r=>{
                        const src = r.chosen==='excel' ? r.fromExcel : (r.fromHistory||r.fromExcel)
                        return (
                          <React.Fragment key={r.key}>
                            <tr className="history-row">
                              <td>
                                <button className="view-btn" aria-label={r.expanded?'收起':'展开'} onClick={()=>toggleExpand(r.key)} title={r.expanded?'收起':'展开'}>
                                  <i className={`fas ${r.expanded?'fa-chevron-up':'fa-chevron-down'}`} />
                                </button>
                              </td>
                              <td><input type="checkbox" checked={r.selected} onChange={()=>onSelectOne(r.key)} /></td>
                              <td className="employee-name">{src.name}</td>
                              <td className="employee-id">{src.id}</td>
                              <td className="city-name">{codeToCityName[src.city]||src.city}</td>
                              <td>{src.startDate||'-'}</td>
                              <td>{{normal:'正常分娩',difficult:'难产',miscarriage:'流产'}[((src.deliveryMethod||'normal') as DeliveryMethod)]}</td>
                              <td>{{single:'单胎',twins:'双胞胎',triplets:'三胞胎',quadruplets:'四胞胎'}[((src.birthType||'single') as BirthType)]}</td>
                              <td>
                                {r.fromHistory ? (r.hasDiff ? <span className="badge" style={{background:'var(--color-error-light)',color:'var(--color-error-dark)'}}>存在差异</span> : <span className="badge" style={{background:'var(--color-success-light)',color:'var(--color-success-dark)'}}>与历史一致</span>) : <span className="badge">无历史</span>}
                              </td>
                              <td>
                                {r.fromHistory ? (
                                  <div style={{ display:'flex', gap:8 }}>
                                    <button className={`view-btn ${r.chosen==='excel'?'active':''}`} onClick={()=>choose(r.key,'excel')}>用导入</button>
                                    <button className={`view-btn ${r.chosen==='history'?'active':''}`} onClick={()=>choose(r.key,'history')}>用历史</button>
                                  </div>
                                ) : <span>-</span>}
                              </td>
                            </tr>
                            {r.expanded && (
                              <tr>
                                <td></td>
                                <td colSpan={9}>
                                  <div className="calculator-form" style={{ paddingTop: 8 }}>
                                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                                      <div className="calculator-card" style={{ boxShadow:'none' }}>
                                        <div className="card-header" style={{ borderBottom:'1px solid var(--color-border)' }}>
                                          <h3 style={{ margin:0, fontSize:14 }}><i className="fas fa-file-import" /> 导入数据</h3>
                                        </div>
                                        <div className="calculator-form" style={{ paddingTop:8 }}>
                                          <div className="search-summary" style={{ flexDirection:'column', alignItems:'flex-start', gap:6 }}>
                                            <div className="summary-item"><strong>城市</strong>：{codeToCityName[r.fromExcel.city]||r.fromExcel.city}</div>
                                            <div className="summary-item"><strong>产假开始时间</strong>：{r.fromExcel.startDate||'-'}</div>
                                            <div className="summary-item"><strong>生育方式</strong>：{{normal:'正常分娩',difficult:'难产',miscarriage:'流产'}[r.fromExcel.deliveryMethod]}</div>
                                            <div className="summary-item"><strong>胎次</strong>：{{single:'单胎',twins:'双胞胎',triplets:'三胞胎',quadruplets:'四胞胎'}[r.fromExcel.birthType]}</div>
                                            <div className="summary-item"><strong>奖励假</strong>：{r.fromExcel.hasExtendedDays?'是':'否'}</div>
                                            {r.fromExcel.deliveryMethod==='miscarriage' && r.fromExcel.pregnancyDuration ? (
                                              <div className="summary-item"><strong>孕周</strong>：{r.fromExcel.pregnancyDuration}</div>
                                            ) : null}
                                            <div className="summary-item"><strong>产假总天数</strong>：{formatNumberDisplay(r.fromExcel.totalAllowanceDays)}</div>
                                            <div className="summary-item"><strong>12月平均工资</strong>：{formatNumberDisplay(r.fromExcel.avgSalary12Months)}</div>
                                            <div className="summary-item"><strong>月基本工资</strong>：{formatNumberDisplay(r.fromExcel.monthlyBaseSalary)}</div>
                                            <div className="summary-item"><strong>政府补贴</strong>：{formatNumberDisplay(r.fromExcel.governmentAmount)}</div>
                                            <div className="summary-item"><strong>津贴补差金额</strong>：{formatNumberDisplay(r.fromExcel.companyCompensation)}</div>
                                            <div className="summary-item"><strong>返还金额</strong>：{formatNumberDisplay(r.fromExcel.employeeRefundAmount)}</div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="calculator-card" style={{ boxShadow:'none' }}>
                                        <div className="card-header" style={{ borderBottom:'1px solid var(--color-border)' }}>
                                          <h3 style={{ margin:0, fontSize:14 }}><i className="fas fa-history" /> 历史数据</h3>
                                        </div>
                                        <div className="calculator-form" style={{ paddingTop:8 }}>
                                          <div className="search-summary" style={{ flexDirection:'column', alignItems:'flex-start', gap:6 }}>
                                            {r.fromHistory ? (
                                              <>
                                                <div className="summary-item"><strong>城市</strong>：{codeToCityName[r.fromHistory?.city||'']||r.fromHistory?.city||'-'}</div>
                                                <div className="summary-item"><strong>产假开始时间</strong>：{r.fromHistory?.startDate||'-'}</div>
                                                <div className="summary-item"><strong>生育方式</strong>：{{normal:'正常分娩',difficult:'难产',miscarriage:'流产'}[((r.fromHistory?.deliveryMethod||'normal') as DeliveryMethod)]}</div>
                                                <div className="summary-item"><strong>胎次</strong>：{{single:'单胎',twins:'双胞胎',triplets:'三胞胎',quadruplets:'四胞胎'}[((r.fromHistory?.birthType||'single') as BirthType)]}</div>
                                                <div className="summary-item"><strong>奖励假</strong>：{r.fromHistory?.hasExtendedDays?'是':'否'}</div>
                                                {r.fromHistory?.deliveryMethod==='miscarriage' && r.fromHistory?.pregnancyDuration ? (
                                                  <div className="summary-item"><strong>孕周</strong>：{r.fromHistory?.pregnancyDuration}</div>
                                                ) : null}
                                                <div className="summary-item"><strong>产假总天数</strong>：{formatNumberDisplay(r.fromHistory?.totalAllowanceDays)}</div>
                                                <div className="summary-item"><strong>12月平均工资</strong>：{formatNumberDisplay(r.fromHistory?.avgSalary12Months)}</div>
                                                <div className="summary-item"><strong>月基本工资</strong>：{formatNumberDisplay(r.fromHistory?.monthlyBaseSalary)}</div>
                                                <div className="summary-item"><strong>政府补贴</strong>：{formatNumberDisplay(r.fromHistory?.governmentAmount)}</div>
                                                <div className="summary-item"><strong>津贴补差金额</strong>：{formatNumberDisplay(r.fromHistory?.companyCompensation)}</div>
                                                <div className="summary-item"><strong>返还金额</strong>：{formatNumberDisplay(r.fromHistory?.employeeRefundAmount)}</div>
                                              </>
                                            ) : (
                                              <div className="subtitle">没有对应历史记录</div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        )})}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {computedCount>0 && (
            <div className="history-table-container" style={{ marginTop: 16 }}>
              <div className="calculator-card">
                <div className="card-header">
                  <h2><i className="fas fa-clipboard-check" /> 计算结果</h2>
                </div>
                <div className="table-responsive">
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th>姓名</th>
                        <th>工号</th>
                        <th>城市</th>
                        <th>产假时间</th>
                        <th>总天数</th>
                        <th>补差金额</th>
                        <th style={{ width: 48 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.filter(r=>r.result).map(r=>{
                        const src = r.chosen==='excel' ? r.fromExcel : (r.fromHistory||r.fromExcel)
                        const res = r.result!
                        return (
                          <>
                            <tr key={r.key} className="history-row">
                              <td className="employee-name">{src.name}</td>
                              <td className="employee-id">{src.id}</td>
                              <td className="city-name">{codeToCityName[src.city]||src.city}</td>
                              <td className="vacation-period">{res.startDate} - {res.endDate}</td>
                              <td className="vacation-days">{res.totalDays} 天</td>
                              <td className="company-compensation">¥{res.allowance.companyCompensation.toFixed(2)}</td>
                              <td>
                                <button className="view-btn" aria-label={r.resultExpanded?'收起详情':'展开详情'} onClick={()=>setRows(prev=>prev.map(x=>x.key===r.key?{...x, resultExpanded:!x.resultExpanded}:x))}>
                                  <i className={`fas ${r.resultExpanded?'fa-chevron-up':'fa-chevron-down'}`} />
                                </button>
                              </td>
                            </tr>
                            {r.resultExpanded && (
                              <tr>
                                <td colSpan={7}>
                                  <div style={{ padding: 12, borderRadius: 8, background: 'linear-gradient(180deg, var(--color-white), var(--color-background-light))', border: '1px solid var(--color-border)' }}>
                                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:12 }}>
                                      <div>
                                        <div style={{ fontSize:12, color:'var(--color-text-secondary)' }}>产假构成</div>
                                        <div style={{ marginTop:6, display:'flex', flexWrap:'wrap', gap:8 }}>
                                          <span className="badge">基础 {res.breakdown.baseDays} 天</span>
                                          <span className="badge">奖励 {res.breakdown.extendedDays} 天</span>
                                          <span className="badge">难产 {res.breakdown.difficultBirthDays} 天</span>
                                          <span className="badge">多胎 {res.breakdown.multipleBirthDays} 天</span>
                                          <span className="badge">流产 {res.breakdown.miscarriageDays} 天</span>
                                        </div>
                                      </div>
                                      <div>
                                        <div style={{ fontSize:12, color:'var(--color-text-secondary)' }}>津贴计算</div>
                                        <ul style={{ marginTop:6, paddingLeft:18 }}>
                                          <li>津贴天数：{res.allowance.allowanceDays} 天</li>
                                          <li>日均工资：¥{res.allowance.dailyRate.toFixed(2)}</li>
                                          <li>B(均薪)：¥{res.allowance.baseByAvgSalary.toFixed(2)}</li>
                                          <li>C(政府)：¥{res.allowance.government.toFixed(2)}</li>
                                          <li>D(取大)：¥{res.allowance.chosenD.toFixed(2)}</li>
                                          {isPayrollCity(src.city) ? <li>A(实发)：¥{res.allowance.paidWageA.toFixed(2)}</li> : <li>非发薪城市</li>}
                                          <li>补差：¥{res.allowance.companyCompensation.toFixed(2)}</li>
                                        </ul>
                                      </div>
                                      <div>
                                        <div style={{ fontSize:12, color:'var(--color-text-secondary)' }}>说明</div>
                                        <ul style={{ marginTop:6, paddingLeft:18 }}>
                                          {res.details.map((d,i)=>(<li key={i}>{d}</li>))}
                                        </ul>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        )})}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </main>

        <footer className="footer">
          <p>© 2025 Twins HR产假津贴管理系统 | 仅供内部使用</p>
        </footer>
      </div>
    </div>
  )
}

export default BatchProcessor
